import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  ReceiptLong as ReceiptLongIcon,
  RequestQuote as RequestQuoteIcon,
  LockOutlined as LockOutlinedIcon,
  Description as DescriptionIcon,
  FactCheck as FactCheckIcon,
  AttachMoney as AttachMoneyIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  tienePermisosModulo,
  puedeEditarProgramaFacturacion,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, StatCard, fpInputSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';
import PrintListado from 'utils/PrintListado';
import {
  CONFIGURACION_IMPRESION_CHASIS,
  esFacturaChasis,
  montoExentoChasis,
  tituloComprobante,
} from 'lib/impresionChasis';
import VistaFactura from './VistaFactura';
import NotaCredito from './NotaCredito';
import NotaDebito from './NotaDebito';
import DetalleEcf, { ChipEstadoEcf } from 'Fiscal/DetalleEcf';

const PROGRAMA_MANTENIMIENTO_FACTURAS = 'mantenimientofacturasemitidas';
const PROGRAMA_NOTA_CREDITO = 'mantenimientonotacredito';
const PROGRAMA_NOTA_DEBITO = 'mantenimientonotadebito';
const RNC_CONSUMIDOR_GENERICO = '999999';

/** Códigos e-CF permitidos en POS (sufijo del nombre, p.ej. "CONSUMO:E32" → 32). */
const CODIGOS_DOCUMENTO_POS = ['31', '32', '44', '45'];

/** Extrae el código numérico del tipo fiscal (campo codigo o sufijo tras ":" en nombre). */
const codigoTipoDocumento = (tipo) => {
  const desdeCodigo = String(tipo?.codigo || '').replace(/\D/g, '');
  if (desdeCodigo) return desdeCodigo;
  return (tipo?.nombre || '').split(':')[1]?.replace(/\D/g, '') || '';
};

const rncCompradorDeFila = (f) => {
  const ncf = String(f?.ncf || '').toUpperCase();
  const esE32 = ncf.startsWith('E32');
  const candidatos = esE32 ? [f?.rnc, f?.suplidor] : [f?.rnc];
  for (const c of candidatos) {
    const v = String(c || '').trim();
    if (v && v !== RNC_CONSUMIDOR_GENERICO) return v;
  }
  return '';
};

const nombreClienteDeFila = (f) => {
  const nombre = String(f?.nombre_cliente || f?.cliente_nombre || '').trim();
  const rnc = rncCompradorDeFila(f);
  if (nombre && nombre !== rnc && nombre !== RNC_CONSUMIDOR_GENERICO) return nombre;
  return '';
};

const MantenimientoFacturasEmitidas = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const {
    nombreNegocio,
    usuarioLog,
    claveLog,
    roleDetalles,
    direccion_empresa: direccionEmpresaCtx,
    rnc_empresa: rncEmpresaCtx,
  } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA_MANTENIMIENTO_FACTURAS);
  const puedeNotaCredito = tienePermisosModulo(roleDetalles, PROGRAMA_NOTA_CREDITO);
  const puedeNotaDebito = tienePermisosModulo(roleDetalles, PROGRAMA_NOTA_DEBITO);

  

  const [facturas, setFacturas] = useState([]);
  const [tiposDocumentos, setTiposDocumentos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [datosEmpresa, setDatosEmpresa] = useState(null);

  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [tipoDocumento, setTipoDocumento] = useState('');
  const [observacion, setObservacion] = useState('');

  const [vistaFacturaAbierta, setVistaFacturaAbierta] = useState(false);
  const [datosVistaFactura, setDatosVistaFactura] = useState({});

  const [notaCreditoAbierta, setNotaCreditoAbierta] = useState(false);
  const [facturaParaNotaCredito, setFacturaParaNotaCredito] = useState(null);

  const [notaDebitoAbierta, setNotaDebitoAbierta] = useState(false);
  const [facturaParaNotaDebito, setFacturaParaNotaDebito] = useState(null);

  const [detalleEcfAbierto, setDetalleEcfAbierto] = useState(false);
  const [facturaDetalleEcf, setFacturaDetalleEcf] = useState(null);

  const cargarTiposDocumentos = useCallback(async () => {
    if (!puedeLeer || !selectedCompany?.db_empresa) return;
    try {
      const response = await fetch(servidor + '/api/facturabackend_listartiposdocumentos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setTiposDocumentos(
          (data.tipos || []).filter(
            (tipo) =>
              tipo.activo === 'S' &&
              CODIGOS_DOCUMENTO_POS.includes(codigoTipoDocumento(tipo))
          )
        );
      }
    } catch {
      showError('Error al cargar tipos de documentos');
    }
  }, [puedeLeer, selectedCompany, usuarioLog, claveLog, servidor, showError]);

  /**
   * Datos de empresa para el encabezado de PrintListado.
   * Prioridad: configuración de factura (nombre, dirección, teléfono, RNC, email)
   * y, si faltan campos, contexto de login (nombreNegocio, direccion_empresa, rnc_empresa).
   */
  const cargarDatosEmpresa = useCallback(async () => {
    if (!puedeLeer || !selectedCompany?.db_empresa) return;
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerconfiguracion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tipo_documento: 'Factura',
        }),
      });
      const config = response.ok
        ? (await response.json().catch(() => null))?.configuracion || null
        : null;

      const nombreCtx =
        typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
          ? nombreNegocio.trim()
          : '';
      const direccionCtx =
        typeof direccionEmpresaCtx === 'string' && direccionEmpresaCtx.trim()
          ? direccionEmpresaCtx.trim()
          : '';
      const rncCtx =
        typeof rncEmpresaCtx === 'string' && rncEmpresaCtx.trim() ? rncEmpresaCtx.trim() : '';

      setDatosEmpresa({
        nombre:
          (config?.nombreEmpresa || '').trim() ||
          selectedCompany?.name ||
          selectedCompany?.empresa ||
          nombreCtx ||
          'Empresa',
        direccion: (config?.direccionEmpresa || '').trim() || direccionCtx,
        telefono: (config?.telefonoEmpresa || '').trim(),
        rnc: (config?.rncEmpresa || '').trim() || rncCtx,
        email: (config?.emailEmpresa || '').trim(),
      });
    } catch {
      const nombreCtx =
        typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
          ? nombreNegocio.trim()
          : '';
      setDatosEmpresa({
        nombre: selectedCompany?.name || selectedCompany?.empresa || nombreCtx || 'Empresa',
        direccion:
          typeof direccionEmpresaCtx === 'string' ? direccionEmpresaCtx.trim() : '',
        telefono: '',
        rnc: typeof rncEmpresaCtx === 'string' ? rncEmpresaCtx.trim() : '',
        email: '',
      });
    }
  }, [
    puedeLeer,
    selectedCompany,
    usuarioLog,
    claveLog,
    servidor,
    nombreNegocio,
    direccionEmpresaCtx,
    rncEmpresaCtx,
  ]);

  useEffect(() => {
    cargarTiposDocumentos();
  }, [cargarTiposDocumentos]);

  useEffect(() => {
    cargarDatosEmpresa();
  }, [cargarDatosEmpresa]);

  const cargarFacturas = useCallback(async () => {
    if (!puedeLeer || !selectedCompany?.db_empresa) return;
    setCargando(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_listarfacturas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          fecha_desde: fechaDesde || undefined,
          fecha_hasta: fechaHasta || undefined,
          tipo_documento: tipoDocumento || undefined,
          observacion: observacion.trim() || undefined,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setFacturas(data.facturas || []);
        if ((data.facturas || []).length === 0) {
          showSuccess('No se encontraron facturas con los filtros aplicados');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar facturas');
      }
    } catch {
      showError('Error de conexión al cargar facturas');
    } finally {
      setCargando(false);
    }
  }, [
    puedeLeer,
    selectedCompany,
    usuarioLog,
    claveLog,
    servidor,
    fechaDesde,
    fechaHasta,
    tipoDocumento,
    observacion,
    showError,
    showSuccess,
  ]);

  const handleLimpiarFiltros = () => {
    setFechaDesde('');
    setFechaHasta('');
    setTipoDocumento('');
    setObservacion('');
  };

  /**
   * Obtiene la configuración (incluyendo `plantillaPorDefecto`) guardada en
   * `configuracion_factura` para el `tipofactura` indicado (p.ej. "FACTURA POS",
   * "COTIZACION"). La clave de búsqueda es EXACTAMENTE el valor de
   * `histresu.tipofactura` que viaja con la factura; no se aplican fallbacks
   * para evitar usar una plantilla distinta a la guardada para ese documento.
   * Si el backend reporta `configuracionGuardada === true` devuelve esa
   * configuración; en caso contrario devuelve la respuesta por defecto (o null
   * si no hay respuesta válida).
   */
  const obtenerConfiguracionPorTipoFactura = useCallback(
    async (tipoFactura) => {
      const clave = (tipoFactura || '').trim();
      if (!clave || !selectedCompany?.db_empresa) return null;
      try {
        const response = await fetch(servidor + '/api/facturabackend_obtenerconfiguracion', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            tipo_documento: clave,
          }),
        });
        if (!response.ok) return null;
        const data = await response.json().catch(() => null);
        return data?.configuracion || null;
      } catch (_) {
        return null;
      }
    },
    [servidor, selectedCompany, usuarioLog, claveLog]
  );

  const reimprimirFactura = async (fila) => {
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    const nfact = fila?.nfact;
    const tipofactura = fila?.tipofactura;
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerfactura', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          nfact,
          tipofactura,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const factura = data.factura || {};
        // La plantilla a usar para reimprimir se determina SIEMPRE por el campo
        // `tipofactura` de la propia factura (p.ej. "FACTURA POS"), que es la
        // clave bajo la que `ConfiguracionFactura` persiste la plantilla por
        // tipo de documento. Como respaldo usamos el `tipofactura` recibido
        // como parámetro de la fila listada.
        const tipoFacturaFactura =
          (factura.tipofactura && String(factura.tipofactura).trim()) ||
          (tipofactura || '').trim();

        // Las facturas de chasis se reimprimen siempre en hoja grande, como al
        // emitirlas; las demás usan el formato configurado en el POS.
        const configuracionImpresion = esFacturaChasis(tipoFacturaFactura)
          ? CONFIGURACION_IMPRESION_CHASIS
          : await obtenerConfiguracionPorTipoFactura(tipoFacturaFactura);

        const rncComprador =
          rncCompradorDeFila(factura) || rncCompradorDeFila(fila);
        const clienteNombre =
          nombreClienteDeFila(factura) || nombreClienteDeFila(fila);
        setDatosVistaFactura({
          ...factura,
          id_empresa: selectedCompany,
          tipoDocumento: tipoFacturaFactura,
          configuracion: configuracionImpresion || undefined,
          // La hoja de chasis lleva el nombre del e-CF como título, no el
          // tipofactura recortado ("FACTURA ELECTRO").
          ...(esFacturaChasis(tipoFacturaFactura) && {
            tituloDocumento:
              tituloComprobante(factura.ncf, factura.codigocomprobante) || 'FACTURA',
            montoExento: montoExentoChasis(factura.items),
          }),
          rnc: rncComprador,
          cliente_nombre: clienteNombre,
        });
        setVistaFacturaAbierta(true);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al obtener la factura');
      }
    } catch {
      showError('Error de conexión al obtener la factura');
    }
  };

  const facturaAdmiteNota = (f) => {
    const tipo = (f?.tipofactura || '').trim().toUpperCase();
    return tipo && !tipo.includes('COTIZ');
  };

  /** e-CF en error o rechazado por la DGII: no admite N/C ni N/D. */
  const ecfImpideNota = (f) => {
    const estado = String(f?.ecf_estado || '').toLowerCase().trim();
    return estado === 'error' || estado === 'rechazado';
  };

  const puedeEmitirNota = (f) => facturaAdmiteNota(f) && !ecfImpideNota(f);

  const tituloTooltipNota = (f, tipoNota) => {
    if (!facturaAdmiteNota(f)) return 'No aplica a cotizaciones';
    if (ecfImpideNota(f)) {
      const estado = String(f?.ecf_estado || '').toLowerCase();
      return estado === 'rechazado'
        ? `No se puede emitir: e-CF rechazado`
        : `No se puede emitir: e-CF con error`;
    }
    return `Emitir nota de ${tipoNota}`;
  };

  const mensajeEcfImpideNota = (f, tipoNota) => {
    const estado = String(f?.ecf_estado || '').toLowerCase();
    if (estado === 'rechazado') {
      return `No se puede emitir nota de ${tipoNota}: el e-CF fue rechazado por la DGII.`;
    }
    return `No se puede emitir nota de ${tipoNota}: el e-CF tiene error.`;
  };

  const abrirNotaCredito = (f) => {
    if (!puedeNotaCredito) {
      showError('No tiene permiso para emitir notas de crédito.');
      return;
    }
    if (!facturaAdmiteNota(f)) {
      showError('Las cotizaciones no admiten notas de crédito.');
      return;
    }
    if (ecfImpideNota(f)) {
      showError(mensajeEcfImpideNota(f, 'crédito'));
      return;
    }
    setFacturaParaNotaCredito({ nfact: f.nfact, tipofactura: f.tipofactura, ncf: f.ncf, ...f });
    setNotaCreditoAbierta(true);
  };

  const abrirDetalleEcf = (f) => {
    if (!f?.ncf) {
      showError('Esta factura no tiene NCF electrónico para consultar.');
      return;
    }
    setFacturaDetalleEcf({
      ...f,
      encf: f.ncf,
      nombre_cliente: nombreClienteDeFila(f),
      rnc_comprador: rncCompradorDeFila(f),
      monto_total: f.total,
      fecha_factura: f.fecha,
      estado: f.ecf_estado,
    });
    setDetalleEcfAbierto(true);
  };

  const onDetalleEcfCerrar = () => {
    setDetalleEcfAbierto(false);
    setFacturaDetalleEcf(null);
  };

  const abrirNotaDebito = (f) => {
    if (!puedeNotaDebito) {
      showError('No tiene permiso para emitir notas de débito.');
      return;
    }
    if (!facturaAdmiteNota(f)) {
      showError('Las cotizaciones no admiten notas de débito.');
      return;
    }
    if (ecfImpideNota(f)) {
      showError(mensajeEcfImpideNota(f, 'débito'));
      return;
    }
    setFacturaParaNotaDebito({ nfact: f.nfact, tipofactura: f.tipofactura, ncf: f.ncf, ...f });
    setNotaDebitoAbierta(true);
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '—';
    const raw = String(fecha).trim();
    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
    try {
      const date = new Date(fecha);
      if (Number.isNaN(date.getTime())) return raw;
      return date.toLocaleDateString('es-DO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return raw;
    }
  };

  const formatearFormasPago = (f) => {
    const partes = [];
    if (parseFloat(f.pagadoef) > 0) partes.push(`Efectivo: ${parseFloat(f.pagadoef).toFixed(2)}`);
    if (parseFloat(f.pagadotc) > 0) partes.push(`Tarjeta: ${parseFloat(f.pagadotc).toFixed(2)}`);
    if (parseFloat(f.pagadoot) > 0) partes.push(`Otros: ${parseFloat(f.pagadoot).toFixed(2)}`);
    if (parseFloat(f.pagadonc) > 0) partes.push(`N/C: ${parseFloat(f.pagadonc).toFixed(2)}`);
    return partes.length ? partes.join(' | ') : f.tipodepago || '—';
  };

  const columnasPrint = useMemo(
    () => [
      { id: 'tipofactura', label: 'Documento' },
      { id: 'nfact', label: 'Nº' },
      { id: 'fecha', label: 'Fecha', align: 'center', render: (_, row) => formatearFecha(row.fecha) },
      {
        id: 'suplidor',
        label: 'Cliente',
        render: (_, row) => nombreClienteDeFila(row) || '—',
      },
      {
        id: 'rnc',
        label: 'RNC',
        render: (_, row) => rncCompradorDeFila(row) || '—',
      },
      {
        id: 'total',
        label: 'Total',
        align: 'right',
        render: (v) =>
          v != null
            ? `$${parseFloat(v).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : '—',
      },
      { id: 'formasPago', label: 'Formas de pago', render: (_, row) => formatearFormasPago(row) },
      { id: 'ncf', label: 'NCF', align: 'center', render: (_, row) => row.ncf || '—' },
      {
        id: 'ecf_estado',
        label: 'Estado ECF',
        align: 'center',
        render: (_, row) => {
          const estado = (row.ecf_estado || '').toLowerCase();
          if (!estado || !row.ncf) return '—';
          return estado.charAt(0).toUpperCase() + estado.slice(1).replace('_', ' ');
        },
      },
    ],
    []
  );

  const onNotaCreditoCerrar = () => {
    setNotaCreditoAbierta(false);
    setFacturaParaNotaCredito(null);
    cargarFacturas();
  };

  const onNotaDebitoCerrar = () => {
    setNotaDebitoAbierta(false);
    setFacturaParaNotaDebito(null);
    cargarFacturas();
  };

  const sumaTotal = facturas.reduce((s, f) => s + (parseFloat(f.total) || 0), 0);
  const sumaPagado = facturas.reduce(
    (s, f) =>
      s +
      (parseFloat(f.pagadoef) || 0) +
      (parseFloat(f.pagadotc) || 0) +
      (parseFloat(f.pagadoot) || 0) +
      (parseFloat(f.pagadonc) || 0),
    0
  );

  const cardShellSx = {
    borderRadius: 2.5,
    border: `1px solid ${FP.color.border}`,
    bgcolor: FP.color.cardBg,
    boxShadow: FP.shadow.soft,
    overflow: 'hidden',
  };

  const filterHeaderSx = {
    px: { xs: 2, md: 3 },
    py: 1.6,
    borderBottom: `1px solid ${FP.color.border}`,
    bgcolor: '#FAFBFE',
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
  };

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    mb: 0.5,
  };

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Facturas emitidas"
          subtitle={nombreNegocio ? `${nombreNegocio} · Mantenimiento` : 'Mantenimiento'}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «mantenimientofacturasemitidas» en roles para acceder al mantenimiento.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Facturas emitidas"
        subtitle={nombreNegocio ? `${nombreNegocio} · Mantenimiento` : 'Mantenimiento'}
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }} id="printable-mantenimiento">
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ReceiptIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros de búsqueda
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Box className="no-print">
              <PrintListado
                title={
                  datosEmpresa?.nombre ||
                  selectedCompany?.name ||
                  selectedCompany?.empresa ||
                  'Empresa'
                }
                empresa={datosEmpresa}
                reportTitle="Reporte de facturas emitidas"
                subtitle={
                  [fechaDesde, fechaHasta].filter(Boolean).length
                    ? `Del ${fechaDesde || '...'} al ${fechaHasta || '...'}`
                    : undefined
                }
                columns={columnasPrint}
                data={facturas}
                emptyMessage="No hay facturas para imprimir"
                printButtonLabel="Imprimir listado"
                printButtonProps={{
                  sx: {
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 1.4,
                    bgcolor: FP.color.navy,
                    color: '#FFFFFF',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                  },
                }}
                footer={`Total facturas: ${facturas.length} | Suma total: $${sumaTotal.toLocaleString(
                  'es-DO',
                  { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                )}`}
              />
            </Box>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }} className="no-print">
            <Grid container spacing={2.5} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel id="mant-fact-tipo-doc" shrink>
                    Documento fiscal
                  </InputLabel>
                  <Select
                    labelId="mant-fact-tipo-doc"
                    value={tipoDocumento}
                    onChange={(e) => setTipoDocumento(e.target.value)}
                    displayEmpty
                    label="Documento fiscal"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {tiposDocumentos.map((t) => (
                      <MenuItem key={t.id || t.nombre || t.codigo} value={t.nombre || t.codigo}>
                        {t.nombre || t.codigo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Observación"
                  value={observacion}
                  onChange={(e) => setObservacion(e.target.value)}
                  placeholder="Buscar en observación"
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={2}>
                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={cargarFacturas}
                    disabled={cargando}
                    sx={{
                      height: 46,
                      bgcolor: FP.color.navy,
                      color: '#FFFFFF',
                      borderRadius: 1.4,
                      textTransform: 'none',
                      fontWeight: 700,
                      boxShadow: 'none',
                      flex: 1,
                      '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                    }}
                  >
                    Buscar
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleLimpiarFiltros}
                    sx={{
                      height: 46,
                      borderColor: FP.color.border,
                      color: FP.color.textPrimary,
                      bgcolor: '#FFFFFF',
                      borderRadius: 1.6,
                      textTransform: 'none',
                      fontWeight: 700,
                      flex: 1,
                      '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                    }}
                  >
                    Limpiar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {facturas.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }} className="no-print">
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Facturas"
                value={String(facturas.length)}
                icon={DescriptionIcon}
                accent={FP.color.accentBlue}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Suma total"
                value={`$${sumaTotal.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                icon={AttachMoneyIcon}
                accent={FP.color.navy}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Cobrado"
                value={`$${sumaPagado.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                icon={FactCheckIcon}
                accent={FP.color.successDark}
              />
            </Grid>
          </Grid>
        )}

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Detalle de facturas emitidas
            </Typography>
            {cargando && (
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', fontWeight: 600 }}>
                Cargando…
              </Typography>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {cargando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} sx={{ color: FP.color.accentBlue }} />
              </Box>
            ) : facturas.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  Use los filtros y pulse «Buscar» para listar las facturas emitidas.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
              >
                <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 1320, sm: 1540 } }}>
                  <colgroup>
                    {['7%', '5%', '7%', '12%', '8%', '7%', '12%', '11%', '8%', '15%'].map((w, i) => (
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Documento
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Nº
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Fecha
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Cliente
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          RNC
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Total
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Formas de pago
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          NCF
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Estado ECF
                        </Typography>
                      </TableCell>
                      <TableCell className="no-print">
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Acciones
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {facturas.map((f) => (
                      <TableRow key={`${f.tipofactura}-${f.nfact}`} hover>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={f.tipofactura || '—'}
                        >
                          {f.tipofactura || '—'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: FP.color.textPrimary }}>{f.nfact}</TableCell>
                        <TableCell align="center">{formatearFecha(f.fecha)}</TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={nombreClienteDeFila(f) || '—'}
                        >
                          {nombreClienteDeFila(f) || '—'}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                          }}
                          title={rncCompradorDeFila(f) || '—'}
                        >
                          {rncCompradorDeFila(f) || '—'}
                        </TableCell>
                        <TableCell align="right">
                          ${(parseFloat(f.total) || 0).toLocaleString('es-DO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={formatearFormasPago(f)}
                        >
                          {formatearFormasPago(f)}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                          }}
                          title={f.ncf || '—'}
                        >
                          {f.ncf || '—'}
                        </TableCell>
                        <TableCell align="center">
                          {!f.ncf ? (
                            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.8rem' }}>—</Typography>
                          ) : (
                            <ChipEstadoEcf estado={f.ecf_estado} />
                          )}
                        </TableCell>
                        <TableCell align="center" className="no-print">
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              justifyContent: 'center',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                            }}
                          >
                            <Tooltip title="Reimprimir">
                              <IconButton
                                size="small"
                                disabled={!usuarioLog}
                                onClick={() => reimprimirFactura(f)}
                                sx={{
                                  color: FP.color.accentBlue,
                                  '&:hover': { bgcolor: FP.color.accentBlueSoft },
                                  '&.Mui-disabled': { color: FP.color.textMuted },
                                }}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={f.ncf ? 'Ver detalle e-CF (aceptación / rechazo)' : 'Sin NCF electrónico'}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!usuarioLog || !f.ncf}
                                  onClick={() => abrirDetalleEcf(f)}
                                  sx={{
                                    color: FP.color.navy,
                                    '&:hover': { bgcolor: FP.color.surfaceAlt },
                                    '&.Mui-disabled': { color: FP.color.textMuted },
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {puedeNotaCredito && (
                              <Tooltip title={tituloTooltipNota(f, 'crédito')}>
                                <span>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ReceiptLongIcon />}
                                    disabled={!usuarioLog || !puedeEmitirNota(f)}
                                    onClick={() => abrirNotaCredito(f)}
                                    sx={{
                                      minWidth: 0,
                                      px: 1,
                                      py: 0.35,
                                      fontSize: '0.72rem',
                                      textTransform: 'none',
                                      fontWeight: 700,
                                      borderRadius: 1.4,
                                      borderColor: FP.color.successDark,
                                      color: FP.color.successDark,
                                      '&:hover': {
                                        bgcolor: FP.color.successSoft,
                                        borderColor: FP.color.successDark,
                                      },
                                      '&.Mui-disabled': {
                                        borderColor: FP.color.border,
                                        color: FP.color.textMuted,
                                      },
                                    }}
                                  >
                                    N/C
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                            {puedeNotaDebito && (
                              <Tooltip title={tituloTooltipNota(f, 'débito')}>
                                <span>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<RequestQuoteIcon />}
                                    disabled={!usuarioLog || !puedeEmitirNota(f)}
                                    onClick={() => abrirNotaDebito(f)}
                                    sx={{
                                      minWidth: 0,
                                      px: 1,
                                      py: 0.35,
                                      fontSize: '0.72rem',
                                      textTransform: 'none',
                                      fontWeight: 700,
                                      borderRadius: 1.4,
                                      borderColor: FP.color.warning,
                                      color: FP.color.warning,
                                      '&:hover': {
                                        bgcolor: FP.color.warningSoft,
                                        borderColor: FP.color.warning,
                                      },
                                      '&.Mui-disabled': {
                                        borderColor: FP.color.border,
                                        color: FP.color.textMuted,
                                      },
                                    }}
                                  >
                                    N/D
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      <VistaFactura
        open={vistaFacturaAbierta}
        onClose={() => setVistaFacturaAbierta(false)}
        facturaData={datosVistaFactura}
      />

      <NotaCredito
        open={notaCreditoAbierta}
        onClose={onNotaCreditoCerrar}
        facturaOrigen={facturaParaNotaCredito}
        puedeModificar={puedeNotaCredito}
      />

      <NotaDebito
        open={notaDebitoAbierta}
        onClose={onNotaDebitoCerrar}
        facturaOrigen={facturaParaNotaDebito}
        puedeModificar={puedeNotaDebito}
      />

      <DetalleEcf
        open={detalleEcfAbierto}
        onClose={onDetalleEcfCerrar}
        encf={facturaDetalleEcf?.ncf || ''}
        factura={facturaDetalleEcf}
        onEstadoActualizado={(actualizado) => {
          const encfActualizado = actualizado?.encf;
          if (!encfActualizado) return;
          setFacturas((prev) =>
            prev.map((item) =>
              item.ncf === encfActualizado
                ? { ...item, ecf_estado: actualizado.estado || item.ecf_estado }
                : item
            )
          );
          setFacturaDetalleEcf((prev) =>
            prev && prev.ncf === encfActualizado
              ? { ...prev, ecf_estado: actualizado.estado || prev.ecf_estado, estado: actualizado.estado }
              : prev
          );
        }}
      />
    </Box>
  );
};

export default MantenimientoFacturasEmitidas;
